import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Grid,
  Stack,
  Box
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { IconPlus } from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MainCard from 'ui-component/cards/MainCard';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import SearchBar from 'ui-component/SearchBar/SearchBar';
import { del, get } from '../../axios/apiService';
import toast from 'react-hot-toast';
import ConfirmActionModal from 'ui-component/Modals/ConfirmActionModal';
import ActionButtons from 'ui-component/actionButton/ActionButtons';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';
import { Visibility } from '@mui/icons-material';

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.User_Management);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const skip = paginationModel.page * paginationModel.pageSize;
      const top = paginationModel.pageSize;

      const sortField = sortModel[0]?.field || '';
      const sortOrder = sortModel[0]?.sort === 'asc' ? 'Ascending' : 'Descending';
      const sortQuery = sortField ? `&$orderby=${sortField}&$orderbydirection=${sortOrder}` : '';

      const searchQuery = search
        ? `&$filter=FirstName~LIKE~${search} OR LastName~LIKE~${search} OR Email~LIKE~${search} OR PhoneNumber~LIKE~${search}`
        : '';

      const response = await get(
        `AdminUsers?$skip=${skip}&$top=${top}${searchQuery}${sortQuery}`
      );

      if (!response?.values) {
        setUsers([]);
        setRowCount(0);
        return;
      }

      setUsers(response.values);
      setRowCount(response.totalCount);
    } catch (err) {
      console.error('Failed to load users', err);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [paginationModel, sortModel, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleDeleteClick = (userId, userName) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeleteModalOpen(false);
    setSelectedUserId('');
    setSelectedUserName('');
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUserId) {
      handleCloseModal();
      return;
    }

    setIsLoading(true);
    try {
      await del(`AdminUsers/${selectedUserId}`);
      toast.success('User deleted successfully');
      handleCloseModal();
      setPaginationModel({ page: 0, pageSize: 10 });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error?.response?.data || 'Failed to delete user');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 1
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 1
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 1
    },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params) => (
        <ActionButtons
          actions={[
            {
              tooltip: "Edit User",
              icon: canEdit ? <EditIcon /> : <Visibility />,
              color: "primary",
              size: "large",
              onClick: () => navigate(`/admin-user-management/edit-user/${params.id}`)
            },
            ...(
              canEdit ? [
                {
                  tooltip: "Delete User",
                  icon: <DeleteIcon />,
                  color: "error",
                  size: "large",
                  onClick: () => handleDeleteClick(params.id, `${params.row.firstName} ${params.row.lastName}`)
                }
              ] : []
            )
          ]}
        />
      ),
      align: 'right',
      width: 120,
      sortable: false
    }
  ];

  return (
    <Box>
      <MainCard title="Admin User Management">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="end" alignItems="center">
              <SearchBar
                value={search}
                onChange={handleSearchChange}
                placeholder="Search"
              />
              {
                canEdit && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/admin-user-management/add-user')}
                    startIcon={<IconPlus />}
                  >
                    Add
                  </Button>
                )
              }
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <DataGrid
              rows={users}
              columns={columns}
              getRowId={(row) => row.id}
              sortingMode="server"
              paginationMode="server"
              rowCount={rowCount}
              pageSizeOptions={[10]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              autoHeight
              disableSelectionOnClick
            />
          </Grid>
        </Grid>
      </MainCard>

      <ConfirmActionModal
        open={deleteModalOpen}
        onClose={handleCloseModal}
        title="Delete User"
        message={`Are you sure you want to delete ${selectedUserName}?`}
        confirmText="Yes, Delete"
        onConfirm={handleDeleteConfirm}
      />
      <LoadingBackdrop open={isLoading} />
    </Box>
  );
};

export default AdminUserManagement;